/**
 * MarketIQ API Client
 * Integra endpoints de Google Images, Google Search, SEO y más
 */

const MARKETIQ_BASE = 'http://marketiq-production-alb-613636800.eu-west-3.elb.amazonaws.com'
const API_KEY = 'test-key-1'

interface MarketIQResponse<T> {
  ok: boolean
  request_id: string
  surface: string
  kind: string
  layer: 'raw' | 'enriched'
  provider: string
  data: T
  meta: {
    fetched_at: string
    cache?: { hit: boolean; ttl?: number }
  }
  errors: Array<{ code: string; message: string }>
}

// ─── Google Images ───────────────────────────────────────────────
export interface GoogleImage {
  position: number
  thumbnail: string
  original: string
  original_width: number
  original_height: number
  title: string
  source: string
  link: string
  is_product: boolean
}

interface GoogleImagesData {
  query: string
  images: GoogleImage[]
}

export async function searchImages(
  query: string,
  opts?: { country?: string; language?: string }
): Promise<GoogleImage[]> {
  const params = new URLSearchParams({ query })
  if (opts?.country) params.set('country', opts.country)
  if (opts?.language) params.set('language', opts.language)

  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-search/images?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<GoogleImagesData> = await res.json()
    if (json.ok && json.data?.images) return json.data.images
    return []
  } catch (e) {
    console.warn('MarketIQ images error:', e)
    return []
  }
}

// ─── Google Search ───────────────────────────────────────────────
export interface GoogleSearchResult {
  position: number
  title: string
  link: string
  snippet: string
  displayed_link?: string
}

interface GoogleSearchData {
  query: string
  organic_results?: GoogleSearchResult[]
}

export async function searchWeb(
  query: string,
  opts?: { country?: string; language?: string }
): Promise<GoogleSearchResult[]> {
  const params = new URLSearchParams({ query })
  if (opts?.country) params.set('country', opts.country)
  if (opts?.language) params.set('language', opts.language)

  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-search/search?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<GoogleSearchData> = await res.json()
    if (json.ok && json.data?.organic_results) return json.data.organic_results
    return []
  } catch (e) {
    console.warn('MarketIQ search error:', e)
    return []
  }
}

// ─── Google News ─────────────────────────────────────────────────
export interface GoogleNewsResult {
  position: number
  title: string
  link: string
  source: string
  date?: string
  snippet?: string
  thumbnail?: string
}

interface GoogleNewsData {
  query: string
  news_results?: GoogleNewsResult[]
}

export async function searchNews(
  query: string,
  opts?: { country?: string; language?: string }
): Promise<GoogleNewsResult[]> {
  const params = new URLSearchParams({ query })
  if (opts?.country) params.set('country', opts.country)
  if (opts?.language) params.set('language', opts.language)

  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-search/news?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<GoogleNewsData> = await res.json()
    if (json.ok && json.data?.news_results) return json.data.news_results
    return []
  } catch (e) {
    console.warn('MarketIQ news error:', e)
    return []
  }
}

// ─── SEO Trends ──────────────────────────────────────────────────
export interface TrendResult {
  query: string
  value?: number
}

export async function searchTrends(query: string): Promise<TrendResult[]> {
  const params = new URLSearchParams({ query })
  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/seo/trends?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<any> = await res.json()
    if (json.ok) return json.data?.interest_over_time ?? []
    return []
  } catch (e) {
    console.warn('MarketIQ trends error:', e)
    return []
  }
}

// ─── Google Maps Search ──────────────────────────────────────────
export interface GoogleMapsPlace {
  title: string
  place_id?: string
  data_id?: string
  address?: string
  rating?: number
  reviews?: number
  type?: string
  thumbnail?: string
  gps_coordinates?: { latitude: number; longitude: number }
  phone?: string
  website?: string
  description?: string
}

interface GoogleMapsData {
  local_results?: GoogleMapsPlace[]
  place_results?: GoogleMapsPlace
}

export async function searchMaps(
  query: string,
  opts?: { lat?: number; lng?: number }
): Promise<GoogleMapsPlace[]> {
  const params = new URLSearchParams({ query })
  if (opts?.lat) params.set('lat', String(opts.lat))
  if (opts?.lng) params.set('lng', String(opts.lng))

  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-maps/search?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<GoogleMapsData> = await res.json()
    if (json.ok && json.data?.local_results) return json.data.local_results
    return []
  } catch (e) {
    console.warn('MarketIQ maps error:', e)
    return []
  }
}

export async function getPlaceDetails(
  query: string,
  placeId?: string
): Promise<GoogleMapsPlace | null> {
  const params = new URLSearchParams({ query })
  if (placeId) params.set('place_id', placeId)

  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-maps/place?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<GoogleMapsData> = await res.json()
    if (json.ok && json.data?.place_results) return json.data.place_results
    return null
  } catch (e) {
    console.warn('MarketIQ place error:', e)
    return null
  }
}

export async function getPlacePhotos(dataId: string): Promise<string[]> {
  const params = new URLSearchParams({ data_id: dataId })
  try {
    const res = await fetch(
      `${MARKETIQ_BASE}/api/v1/raw/google-maps/photos?${params}`,
      { headers: { 'X-API-Key': API_KEY } }
    )
    const json: MarketIQResponse<any> = await res.json()
    if (json.ok && json.data?.photos) {
      return json.data.photos.map((p: any) => p.image ?? p.thumbnail).filter(Boolean)
    }
    return []
  } catch (e) {
    console.warn('MarketIQ photos error:', e)
    return []
  }
}

// ─── Caché general en memoria ────────────────────────────────────
const cache = new Map<string, any>()

export async function getCachedImages(query: string, opts?: { country?: string; language?: string }): Promise<GoogleImage[]> {
  const cacheKey = `img_${query}_${opts?.language ?? ''}_${opts?.country ?? ''}`
  if (cache.has(cacheKey)) return cache.get(cacheKey)!

  const images = await searchImages(query, opts)
  if (images.length > 0) cache.set(cacheKey, images)
  return images
}

export async function getCachedMaps(query: string): Promise<GoogleMapsPlace[]> {
  const cacheKey = `maps_${query}`
  if (cache.has(cacheKey)) return cache.get(cacheKey)!

  const places = await searchMaps(query)
  if (places.length > 0) cache.set(cacheKey, places)
  return places
}

export async function getCachedNews(query: string, lang: string): Promise<GoogleNewsResult[]> {
  const cacheKey = `news_${query}_${lang}`
  if (cache.has(cacheKey)) return cache.get(cacheKey)!

  const news = await searchNews(query, { language: lang })
  if (news.length > 0) cache.set(cacheKey, news)
  return news
}

// ─── Helper: imagen de un topic de buceo ─────────────────────────
export async function getDivingTopicImage(
  topic: string,
  lang: string = 'es'
): Promise<string | null> {
  const query = lang === 'es'
    ? `buceo ${topic} submarino`
    : `scuba diving ${topic} underwater`

  const images = await getCachedImages(query, { language: lang })
  return images[0]?.original ?? images[0]?.thumbnail ?? null
}

// ─── Helper: múltiples imágenes para galería ─────────────────────
export async function getDivingGallery(
  topic: string,
  count: number = 6,
  lang: string = 'es'
): Promise<GoogleImage[]> {
  const query = lang === 'es'
    ? `buceo ${topic} submarino fotografía`
    : `scuba diving ${topic} underwater photography`

  const images = await getCachedImages(query, { language: lang })
  return images.slice(0, count)
}

// ─── Batch preload: carga varias queries a la vez ────────────────
export async function preloadImageBatch(queries: string[], lang: string = 'es'): Promise<Map<string, GoogleImage[]>> {
  const results = new Map<string, GoogleImage[]>()
  const promises = queries.map(async (q) => {
    const imgs = await getCachedImages(q, { language: lang })
    results.set(q, imgs)
  })
  await Promise.allSettled(promises)
  return results
}
